import { useMemo, useRef, useState } from 'react';
import { MentionUser } from '@/types/notification';
import { MentionSelection, buildStructuredComment } from '@/lib/utils/mention';

interface UseMentionsOptions {
  users: MentionUser[];
}

interface UseMentionsResult {
  displayText: string;
  setDisplayText: (val: string) => void;
  selectedMentions: MentionSelection[];
  setSelectedMentions: (updater: (prev: MentionSelection[]) => MentionSelection[]) => void;
  cursorPosition: number;
  setCursorPosition: (pos: number) => void;
  textareaHeight: number;
  setTextareaHeight: (h: number) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;

  // Handlers
  handleTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleCursorChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>, onSubmit: () => void) => void;

  // Overlay rendering helper
  shouldHideText: boolean;

  // Autocomplete bindings
  isVisible: boolean;
  position: { bottom: number; left: number };
  searchQuery: string;
  selectedIndex: number;
  setSelectedIndex: (i: number) => void;
  showAutocomplete: (pos: { bottom: number; left: number }, query: string) => void;
  hideAutocomplete: () => void;
  selectUser: (user: MentionUser) => void;

  // Submission helper
  toStructured: () => string;
}

export function useMentions(options: UseMentionsOptions): UseMentionsResult {
  const { users } = options;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [cursorPosition, setCursorPosition] = useState(0);
  const [textareaHeight, setTextareaHeight] = useState(40);
  const [displayText, setDisplayText] = useState('');
  const [selectedMentions, setSelectedMentions] = useState<MentionSelection[]>([]);

  // Mention autocomplete state
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ bottom: 0, left: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const showAutocomplete = (pos: { bottom: number; left: number }, query: string) => {
    setPosition(pos);
    setSearchQuery(query);
    setSelectedIndex(0);
    setIsVisible(true);
  };

  const hideAutocomplete = () => {
    setIsVisible(false);
    setSearchQuery('');
    setSelectedIndex(0);
  };

  const selectUser = (user: MentionUser) => {
    const displayMention = `@${user.name}`;
    const structuredMention = `@[${user.name}](user:${user.id})`;
    const beforeCursor = displayText.substring(0, cursorPosition);
    const afterCursor = displayText.substring(cursorPosition);

    const atIndex = beforeCursor.lastIndexOf('@');
    if (atIndex !== -1) {
      const userIdMatch = structuredMention.match(/\(user:([a-f0-9-]+)\)/);
      const userId = userIdMatch ? userIdMatch[1] : '';
      const name = displayMention.startsWith('@') ? displayMention.slice(1) : displayMention;

      const newDisplayText =
        beforeCursor.substring(0, atIndex) + displayMention + ' ' + afterCursor;

      setDisplayText(newDisplayText);
      setSelectedMentions((prev) => [...prev, { name, userId }]);

      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = atIndex + displayMention.length + 1; // after mention + space
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }

    hideAutocomplete();
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const value = e.target.value;
    const positionIndex = e.target.selectionStart || 0;
    setCursorPosition(positionIndex);
    setDisplayText(value);

    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    const newHeight = Math.max(40, Math.min(200, textarea.scrollHeight));
    textarea.style.height = `${newHeight}px`;
    setTextareaHeight(newHeight);

    const textBeforeCursor = value.substring(0, positionIndex);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch && users.length > 0) {
      const rect = e.target.getBoundingClientRect();
      showAutocomplete(
        {
          bottom: window.innerHeight - rect.top,
          left: rect.left,
        },
        mentionMatch[1],
      );
    } else {
      hideAutocomplete();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    onSubmit: () => void,
  ): void => {
    if (isVisible) {
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const handleCursorChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setCursorPosition(e.target.selectionStart || 0);
  };

  const shouldHideText = useMemo(() => displayText.length > 0, [displayText]);
  const toStructured = (): string => buildStructuredComment(displayText, selectedMentions);

  return {
    displayText,
    setDisplayText,
    selectedMentions,
    setSelectedMentions,
    cursorPosition,
    setCursorPosition,
    textareaHeight,
    setTextareaHeight,
    textareaRef,

    handleTextareaChange,
    handleCursorChange,
    handleKeyDown,

    shouldHideText,

    isVisible,
    position,
    searchQuery,
    selectedIndex,
    setSelectedIndex,
    showAutocomplete,
    hideAutocomplete,
    selectUser,

    toStructured,
  };
}

export default useMentions;
